import { Component, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { LocalizationService, LogService, MibpHttpApi, NoticebarService, FrontendContextService, PermissionService, permissionPolicyKey } from 'root/services';
import { NoticeType } from '../noticebar/noticebar.enum';
import { firstValueFrom } from 'rxjs';
import { AddItemToCartSource, PriceAndAvailabilitySource, ProductAvailability, ProductExpectedDate, ProductExpectedDateRequestDto, ProductPrice, ProductType, UpSellCrossSellProductRequestResponseDto } from 'root/mibp-openapi-gen/models';
import { CommonModule } from '@angular/common';
import { ResourceStringComponent } from 'root/shared/components/localization/resource-string/resource-string.component';
import { LoaderComponent } from 'root/shared/components/status/loader/loader.component';
import { ImageComponent } from 'root/components/image/image.component';
import { ExpectedDeliveryDateStateComponent } from 'src/app/shared/components/status/expected-delivery-date-state/expected-delivery-date-state.component';
import { upSellsFnGetUpsellProducts } from "root/mibp-openapi-gen/fn/up-sells/up-sells-fn-get-upsell-products";
import { cartsFnGetActive } from "root/mibp-openapi-gen/fn/carts/carts-fn-get-active";
import { biDashboardFnUpSellUpgradeProduct } from "root/mibp-openapi-gen/fn/bi-dashboard/bi-dashboard-fn-up-sell-upgrade-product";
import { cartsFnReplaceProductsWithUpsellProduct } from "root/mibp-openapi-gen/fn/carts/carts-fn-replace-products-with-upsell-product";
import { MibpApiService } from "root/mibp-openapi-gen/mibp-api.service";
import { PriceStateComponent } from 'src/app/shared/components/status/price-state/price-state.component';
import { AvailabilityStateComponent } from 'src/app/shared/components/status/availability-state/availability-state.component';
import { SupportTeamProductStateMenuComponent } from 'src/app/shared/components/support-team/support-team-product-state-menu/support-team-product-state-menu.component';
import { PriceStateContextDirective } from 'src/app/shared/directives/status/price-state-context/price-state-context.directive';

export interface UpSellProductWithPriceAndAvailability extends UpSellCrossSellProductRequestResponseDto {
  productPrice : ProductPrice;
  productAvailability : ProductAvailability;
  productExpectedDate : ProductExpectedDate;
}

@Component({
  selector: 'mibp-upsell',
  templateUrl: './upsell.component.html',
  styleUrls: ['./upsell.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    ResourceStringComponent,
    LoaderComponent,
    ImageComponent,

    SupportTeamProductStateMenuComponent,
    PriceStateComponent,
    AvailabilityStateComponent,
    ExpectedDeliveryDateStateComponent,
    PriceStateContextDirective
  ]
})
export class UpsellComponent implements OnInit {
  private mibpApiService = inject(MibpApiService);
  private localizationService = inject(LocalizationService);
  private noticeBarService = inject(NoticebarService);
  private logger = inject(LogService);
  private frontendContext = inject(FrontendContextService);
  private httpApi = inject(MibpHttpApi);
  private permissions = inject(PermissionService);
  private log = inject(LogService).withPrefix('upsell.component');
  protected priceAndAvailabilitySource = PriceAndAvailabilitySource.ActiveCart;
  isLoading = false;
  productPrice: ProductPrice;
  upsellProducts: UpSellProductWithPriceAndAvailability[];
  contactUsMessageRequestPrice: string;
  currentUpsellCrossSellProductId: number;
  canSeeAvailability: boolean;
  protected canSeeExpectedDeliveryDate: boolean;
  @Output() closeDialogBox = new EventEmitter<boolean>();
  @Input()
  set setUpsellCrossSellProductId(upsellCrossSellProductId: number) {
    if (!upsellCrossSellProductId) {
      this.upsellProducts = [];
      return;
    }

    this.getUpsellProducts(upsellCrossSellProductId);
  }

  @Output() productHasBeenUpgradedEvent = new EventEmitter();

  constructor() {
    const logger = this.logger;

    this.log = logger.withPrefix('upsell-component');
  }

  ngOnInit(): void {
    this.localizationService.getResourceString('Carts_PriceForProductMessage').then((resourceStringValue) => {
      this.contactUsMessageRequestPrice = resourceStringValue;
    });
    this.canSeeAvailability = this.permissions.test(permissionPolicyKey('canSeeAvailability'));
    this.canSeeExpectedDeliveryDate = this.permissions.test(permissionPolicyKey('canSeeExpectedDeliveryDate'));
  }
  getUpsellProducts(upsellCrossSellProductId: number): void {
    this.currentUpsellCrossSellProductId = upsellCrossSellProductId;
    this.log.debug('getUpsellProducts upsellCrossSellProductId', upsellCrossSellProductId);

    this.isLoading = true;

    this.upsellProducts = [];
    const languageCode = this.localizationService.getLang();

    firstValueFrom(this.mibpApiService.invoke(upSellsFnGetUpsellProducts, {
      upsellCrossSellProductId: upsellCrossSellProductId,
      languageCode: languageCode})).then((upsellProducts) => {
      this.log.debug('response upsellProducts', upsellProducts);
      this.upsellProducts = upsellProducts as UpSellProductWithPriceAndAvailability[];
      this.isLoading = false;

    }).catch(error => {
      this.isLoading = false;
      this.log.error('Error when fetching upsell products', error);
      this.noticeBarService.show('Upsell_ErrorFetchingUpsellProducts', NoticeType.Error);
    });
  }


  async onUpgradeProduct(upsellProduct: UpSellProductWithPriceAndAvailability): Promise<void> {
    this.isLoading = true;
    try {
      //Send bi dashboard event
      const cart = await firstValueFrom(this.mibpApiService.invoke(cartsFnGetActive)); 
      await firstValueFrom(this.mibpApiService.invoke(biDashboardFnUpSellUpgradeProduct, {
        listId: cart?.id,  
        itemId: upsellProduct.originatingUpsellProductId,
        productCode: upsellProduct.code,
        productType: ProductType[upsellProduct.productType],
        source: AddItemToCartSource[AddItemToCartSource.Upsell]
      }));
    } catch (error) {
      this.log.error('Error tracking upsell upgrade product', error);
    }
    firstValueFrom(this.mibpApiService.invoke(cartsFnReplaceProductsWithUpsellProduct, {productIdToReplace : upsellProduct.originatingUpsellProductId.toString(), upsellCrossSellProductId: upsellProduct.id})).then(() => {
      this.productHasBeenUpgradedEvent.emit();
      setTimeout(() => {
        this.isLoading = false;
      }, 3000);

    }, error => {
      this.isLoading = false;
      this.log.error('Error when replacing with upsell product', error);
      this.noticeBarService.show('Upsell_ErrorUpgradeMessage', NoticeType.Error);
    });
  }
  protected onClickProductImage(productCode:string): void
  {
    this.closeDialogBox.emit(true);
    this.frontendContext.Navigation.navigateToProductDetailsPage(productCode);
  }
}
